import { Clock, DollarSign, Film, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/common/Button';
import { useLivelloIndietro } from '@/hooks/useLivelloIndietro';
import { cn } from '@/lib/cn';
import { useHaptics } from '@/hooks/useHaptics';
import type { AvatarVideoClip } from '@/services/avatarApi';
import { useLinguaFormato } from '@/hooks/useLinguaFormato';
import { formatData, formatOra } from '@/lib/fmt';

interface Props {
  open: boolean;
  clip: AvatarVideoClip | null;
  onClose: () => void;
}

/**
 * The chroma-key path on the backend prefixes the user's motion prompt with
 * a long magenta-background system instruction (see video2dService.ts ::
 * CHROMA_KEY_PROMPT_PREFIX). We re-recognize that exact prefix here to split
 * the persisted prompt back into "system" and "user" portions for display.
 * Keep this string verbatim — any drift loses the split.
 */
const CHROMA_KEY_PROMPT_PREFIX =
  `On a perfectly uniform, pure solid magenta background (hex #FF00FF, 100% saturation, no gradients, no shadows on the background). ` +
  `Critical: the subject and its clothing/props must NOT contain any magenta, pink, hot pink, or pinkish-purple anywhere — replace any such colors with neutral tones. ` +
  `Action: `;

const PROVIDER_LABEL: Record<string, string> = {
  'hailuo-fal': 'Hailuo (MiniMax)',
  'kling-fal': 'Kling 2.1',
  'pixverse-fal': 'PixVerse V6',
  'vidu-fal': 'Vidu Q3',
  'veo31-fal': 'Veo 3.1 Standard',
  'veo31-fast-fal': 'Veo 3.1 Fast',
};

interface PromptSplit {
  systemPrompt: string | null;
  userPrompt: string;
}

function splitPrompt(prompt: string): PromptSplit {
  if (prompt.startsWith(CHROMA_KEY_PROMPT_PREFIX)) {
    return {
      systemPrompt: CHROMA_KEY_PROMPT_PREFIX.trim(),
      userPrompt: prompt.slice(CHROMA_KEY_PROMPT_PREFIX.length).trim(),
    };
  }
  return { systemPrompt: null, userPrompt: prompt };
}

function formatTimestamp(iso: string | undefined, lingua: string): string {
  if (!iso) return '—';
  return `${formatData(iso, undefined, lingua)} ${formatOra(iso, lingua)}`;
}

function formatDuration(startedAt?: string, completedAt?: string): string {
  if (!startedAt || !completedAt) return '—';
  const ms = new Date(completedAt).getTime() - new Date(startedAt).getTime();
  if (ms < 0) return '—';
  return `${Math.round(ms / 1000)}s`;
}

export function VideoClipDetailsModal({ open, clip, onClose }: Props) {
  useLivelloIndietro(open && clip !== null, onClose);
  const haptics = useHaptics();
  const lingua = useLinguaFormato();
  const { t } = useTranslation('avatars');
  if (!open || !clip) return null;

  const { systemPrompt, userPrompt } = splitPrompt(clip.prompt);
  const providerLabel = PROVIDER_LABEL[clip.provider] ?? clip.provider;
  const transparencyLabels: Record<string, string> = {
    'none': t('clipDetails.transparency.none'),
    'chroma-key': t('clipDetails.transparency.chromaKey'),
    'matting': t('clipDetails.transparency.matting'),
  };
  const transparencyLabel = transparencyLabels[clip.transparency ?? 'none'] ?? clip.transparency ?? 'none';
  const costUsd = typeof clip.costCents === 'number' ? (clip.costCents / 100).toFixed(2) : null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-bg/95 backdrop-blur">
      <header className="flex items-center justify-between px-4 py-4">
        <div className="flex items-center gap-2">
          <Film aria-hidden className="h-5 w-5 text-brand" />
          <div>
            <h1 className="text-base font-bold text-fg">{clip.label ?? t('clipDetails.fallbackTitle')}</h1>
            <p className="text-[11px] text-fg-muted">{t('clipDetails.subtitle')}</p>
          </div>
        </div>
        <Button
          variant="icona"
          size={36}
          tono="pieno"
          aria-label={t('common.close')}
          onClick={() => { haptics.impact('light'); onClose(); }}
        >
          <X aria-hidden className="h-5 w-5" />
        </Button>
      </header>

      <div className="flex-1 overflow-y-auto px-4 pb-8">
        <div className="flex flex-col gap-5">
          {/* User prompt */}
          <Section title={t('clipDetails.userPrompt')}>
            <p className="whitespace-pre-wrap rounded-xl bg-bg-secondary p-3 text-sm leading-relaxed text-fg">
              {userPrompt || '—'}
            </p>
          </Section>

          {/* System prompt (only when chroma-key wrapper was applied) */}
          {systemPrompt && (
            <Section title={t('clipDetails.systemPrompt')} subtitle={t('clipDetails.systemPromptHint')}>
              <p className="whitespace-pre-wrap rounded-xl border border-amber-500/40 bg-amber-500/5 p-3 text-xs leading-relaxed text-fg-muted">
                {systemPrompt}
              </p>
            </Section>
          )}

          {/* Model + options */}
          <Section title={t('clipDetails.modelOptions')}>
            <div className="flex flex-col gap-1 rounded-xl bg-bg-secondary p-3 text-sm">
              <Row label={t('clipDetails.engine')} value={providerLabel} />
              <Row label={t('clipDetails.duration')} value={clip.durationSec ? `${clip.durationSec}s` : '—'} />
              <Row label={t('clipDetails.resolution')} value={clip.resolution ?? '—'} />
              <Row label={t('clipDetails.background')} value={transparencyLabel} />
              {clip.chromaKeyHex && (
                <Row label={t('clipDetails.keyColor')} value={clip.chromaKeyHex.toUpperCase()} mono />
              )}
              <Row label={t('clipDetails.status')} value={clip.status} mono />
            </div>
          </Section>

          {/* Cost + timing */}
          <Section title={t('clipDetails.run')}>
            <div className="flex flex-col gap-1 rounded-xl bg-bg-secondary p-3 text-sm">
              <Row
                label={t('clipDetails.cost')}
                value={costUsd ? `$${costUsd}` : '—'}
                icon={<DollarSign aria-hidden className="h-3.5 w-3.5" />}
              />
              <Row
                label={t('clipDetails.started')}
                value={formatTimestamp(clip.startedAt, lingua)}
                icon={<Clock aria-hidden className="h-3.5 w-3.5" />}
              />
              <Row
                label={t('clipDetails.completed')}
                value={formatTimestamp(clip.completedAt, lingua)}
                icon={<Clock aria-hidden className="h-3.5 w-3.5" />}
              />
              {clip.startedAt && clip.completedAt && (
                <Row label={t('clipDetails.wallTime')} value={formatDuration(clip.startedAt, clip.completedAt)} />
              )}
            </div>
          </Section>

          {/* Output URLs */}
          {(clip.videoUrl || clip.matteRgbUrl || clip.webmAlphaUrl) && (
            <Section title={t('clipDetails.output')}>
              <div className="flex flex-col gap-1 rounded-xl bg-bg-secondary p-3 text-xs">
                {clip.videoUrl && <FileRow label={t('clipDetails.fileMp4')} url={clip.videoUrl} />}
                {clip.matteRgbUrl && <FileRow label={t('clipDetails.fileRgb')} url={clip.matteRgbUrl} />}
                {clip.matteAlphaUrl && <FileRow label={t('clipDetails.fileAlpha')} url={clip.matteAlphaUrl} />}
                {clip.webmAlphaUrl && <FileRow label={t('clipDetails.fileWebm')} url={clip.webmAlphaUrl} />}
              </div>
            </Section>
          )}

          {/* Failure */}
          {clip.failureReason && (
            <Section title={t('clipDetails.failure')}>
              <p className="whitespace-pre-wrap rounded-xl border border-red-500/40 bg-red-500/10 p-3 text-xs text-fg">
                {clip.failureReason}
              </p>
            </Section>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-1.5">
      <div className="flex flex-col">
        <h2 className="text-xs font-semibold uppercase text-fg-muted">{title}</h2>
        {subtitle && <p className="text-[11px] text-fg-muted/70">{subtitle}</p>}
      </div>
      {children}
    </section>
  );
}

function Row({ label, value, icon, mono }: { label: string; value: string; icon?: React.ReactNode; mono?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1">
      <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase text-fg-muted">
        {icon}
        {label}
      </span>
      <span className={cn('text-right text-fg', mono && 'font-mono text-xs')}>{value}</span>
    </div>
  );
}

function FileRow({ label, url }: { label: string; url: string }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="flex items-center justify-between gap-2 rounded-lg bg-bg-tertiary px-2 py-1.5 transition-opacity active:opacity-80"
    >
      <span className="font-semibold text-fg">{label}</span>
      <span className="line-clamp-1 max-w-[60%] font-mono text-[10px] text-fg-muted">{url.split('/').slice(-2).join('/')}</span>
    </a>
  );
}
